<script>
import { mapGetters } from 'vuex';
import { isPrerender } from '@utils/is-prerender';
import { SwiperSlide, Swiper } from 'vue-awesome-swiper';

import Layout from '@layouts/main.vue';

import Resizable from '@src/mixins/resizable.js';
import Translatable from '@src/mixins/translatable.js';
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js';
import SeoMixin from '@src/mixins/seo.js';

import 'swiper/css/swiper.css';

export default {
  components: {
    Layout,
    SwiperSlide,
    Swiper,
  },
  mixins: [Translatable, Resizable, SanitizerMixin, SeoMixin],
  // page: {
  //   title: 'Article',
  //   meta: [{ name: 'description', content: 'Article Page.' }],
  // },
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      swiperOptions: {
        autoplay: { delay: 5000 },
        speed: 1300,
        allowTouchMove: true,
        loop: true,
        slidesPerView: 1,
        slidesPerGroup: 1,
        pagination: {
          el: '.header-pagination',
          clickable: true,
          bulletClass: 'bullet',
          bulletActiveClass: 'bullet-active',
        },
      },
    };
  },
  computed: {
    ...mapGetters('currentNews', ['dataContacts', 'getArticle', 'previousArticle', 'nextArticle']),
    ...mapGetters('translations', ['currentLanguage']),
    isPrerendering() {
      return isPrerender();
    },
    seoEntity() {
      return this.article;
    },

    article() {
      return this.getArticle(this.slug) || {};
    },
    pageImg() {
      if (!this.article.images && !this.article.webimages) return [];
      const images = this.desktopScreen ? this.article.webimages : this.article.images;
      return Array.isArray(images) ? images : [];
    },
  },

  watch: {
    article: {
      immediate: true,
      handler(newVal) {
        if (newVal && (newVal.slugs || newVal.urlSlug)) {
          const slugs = newVal.slugs || newVal.urlSlug;
          this.$store.commit('translations/SET_ALTERNATE_SLUGS', slugs);
        }
      },
    },
  },

  async created() {
    const articlesLoaded = this.$store.state.currentNews.articles?.length > 0;

    if (!articlesLoaded) {
      await this.$store.dispatch('currentNews/fetchArticles');
    }
  },

  methods: {
    getBackground(image) {
      return {
        backgroundImage: `url(${image})`,
        backgroundSize: 'cover',
      };
    },
    getSeoText(obj) {
      if (!obj) return '';
      return obj[this.currentLanguage] || obj.en || '';
    },
  },
};
</script>

<template>
  <Layout>
    <section class="header-slider">
      <Swiper v-if="pageImg && pageImg.length > 1 && !isPrerendering" :options="swiperOptions">
        <SwiperSlide v-for="(imageObj, index) in pageImg" :key="index">
          <div
            class="slide"
            :style="getBackground(imageObj.url)"
            :title="getSeoText(imageObj.title)"
            role="img"
            :aria-label="getSeoText(imageObj.alt)"
          ></div>
        </SwiperSlide>
        <div slot="pagination" class="header-pagination"></div>
      </Swiper>

      <div
        v-else-if="pageImg && pageImg.length >= 1"
        class="slide"
        :style="getBackground(pageImg[0].url)"
        :title="getSeoText(pageImg[0].title)"
        role="img"
        :aria-label="getSeoText(pageImg[0].alt)"
      ></div>
    </section>

    <section class="justify-center">
      <div class="container header-content" style="height: 135px">
        <div class="title-block">
          <h1 class="header-title">{{ getTranslatedContent('title', article.content) }}</h1>
          <h3 class="header-subtitle">{{ article.datePublished | ddmmYYYY }}</h3>
        </div>
      </div>
    </section>

    <section class="justify-center">
      <div class="container main-content">
        <router-link :to="$l('news_whatson')" class="button-container">
          <button class="button">{{ getTranslatedContent('ARTICLE_all_news_btn') }}</button>
        </router-link>
        <div class="text" v-html="description(article.content)"></div>
      </div>
    </section>

    <section class="justify-center">
      <div class="container navigation">
        <router-link
          v-if="previousArticle(article)"
          :to="$l('news_detail', { slug: previousArticle(article) })"
          class="article-nav-btn"
        >
          <div class="previous-article">
            <i></i>
            <p v-html="getTranslatedContent('ARTICLE_previous')"></p>
          </div>
        </router-link>
        <div v-else class="article-nav-btn disabled">
          <div class="previous-article">
            <i></i>
            <p>...</p>
          </div>
        </div>

        <router-link
          v-if="nextArticle(article)"
          :to="$l('news_detail', { slug: nextArticle(article) })"
          class="article-nav-btn next-article"
        >
          <div class="next-article">
            <p v-html="getTranslatedContent('ARTICLE_next')"></p>
            <i></i>
          </div>
        </router-link>
        <div v-else class="article-nav-btn next-article disabled">
          <div class="next-article">
            <p>...</p>
            <i></i>
          </div>
        </div>
      </div>
    </section>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.main-content {
  margin-top: 20px;
}

.button-container {
  display: flex;
  justify-content: flex-start;
  text-decoration: none;
  width: 100%;

  .button {
    min-width: 98px;
    padding: 8px 10px;
    height: 32px;
    border: 1px solid $dark-blue;
    background: $white;
    border-radius: 5px;
    color: $dark-blue;
    font-family: Open Sans semibold, sans-serif;
    font-weight: 600;
    font-style: normal;
    text-transform: uppercase;
    font-size: 12px;
    line-height: 16px;

    &:hover {
      border: 1px solid $dark-gray;
    }
  }
}

::v-deep .text {
  margin-top: 15px;
  font-family: Open Sans, sans-serif;
  font-weight: normal;
  font-size: 14px;
  line-height: 23px;
  color: $black;
  // opacity: 0.8;
  user-select: none;
  & p:empty {
    height: 15px;
    margin: 0;
  }

  & p:not(:last-child):not(:empty) {
    margin-bottom: 15px;
  }

  & p > br {
    display: none;
  }
  & p:has(br:last-child)::after {
    content: '';
    display: block;
    height: 15px;
  }

  & em {
    font-style: italic;
  }

  & ul {
    margin-left: 20px;
    list-style: initial;
  }

  & strong {
    font-family: Open Sans Bold, sans-serif;
    font-style: normal;
    font-weight: bold;
  }

  & a {
    color: $gray;
    text-decoration: none;

    &:hover {
      color: $orange;
    }
  }

  & .ql-align-center {
    text-align: center;
  }
}

.navigation {
  margin-top: 40px;
  display: flex;
  flex-direction: row;

  & .previous-article,
  & .next-article {
    display: flex;
    flex-direction: row;
    align-items: center;

    cursor: pointer;

    p {
      max-width: 72px;
      font-family: Open Sans, sans-serif;
      font-style: normal;
      font-weight: normal;
      font-size: 12px;
      line-height: 16px;
      text-transform: uppercase;
      color: $dark-blue;
    }

    i {
      display: block;
      width: 24px;
      height: 24px;
    }
  }

  & .previous-article {
    p {
      margin-left: 15px;
      text-align: left;
    }

    i {
      background: url('../../../assets/icons/left-arrow-orange.svg') no-repeat center;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  & .next-article {
    margin-left: auto;

    p {
      margin-right: 15px;
      text-align: right;
    }

    i {
      background: url('../../../assets/icons/right-arrow-orange.svg') no-repeat center;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  .article-nav-btn {
    display: block;
    text-decoration: none;
    user-select: none;
  }
}

.disabled {
  pointer-events: none;
  opacity: 0.2;
}

.header-slider {
  min-height: 550px;
}

::v-deep .header-pagination {
  @extend .pagination;
  position: absolute;
  bottom: 9px;

  & .bullet {
    background: $white;
    opacity: 0.8;
  }

  & .bullet-active {
    background: $orange;
    opacity: 1;
  }
}

.bullet {
  background: $white;
  opacity: 0.8;
}

::v-deep .swiper-wrapper,
.swiper-container {
  z-index: auto;
}

.slide {
  height: 550px;
  background-repeat: no-repeat;
  background-position: center;
  z-index: -1;
}

.header-content {
  position: absolute;
  top: 217px;
}

.title-block {
  width: 840px;
  min-height: 135px;
  height: auto;
  background: rgba(37, 44, 62, 0.36);
  padding: 9px 35px 20px 22px;
  box-sizing: border-box;

  .header-title {
    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 48px;
    line-height: 62px;
    text-transform: uppercase;
    color: $white;
    margin: 0;
  }

  .header-subtitle {
    font-family: Open Sans Semibold, sans-serif;
    font-weight: 600;
    font-size: 22px;
    line-height: 29px;
    color: $white;
  }
}

@media only screen and (max-width: $screen-width-xs) {
  .container {
    width: 100%;
  }

  .header-slider,
  .slide {
    min-height: 274px;
    height: 244px;
  }

  .header-pagination {
    bottom: 5px;
  }

  .header-content {
    top: 70px;
  }

  .container.header-content {
    padding: 0;
  }

  .title-block {
    width: 100%;
    min-height: 125px;
    height: auto;
    padding: 5px 13px;

    .header-title {
      font-size: 26px;
      line-height: 34px;
      margin: 10px 8px 10px 0;
    }

    .header-subtitle {
      font-size: 16px;
      line-height: 21px;
      margin: 0;
    }
  }

  .main-content {
    margin-top: 15px;
  }
}
</style>
